import React, { useEffect, useRef, useState } from "react";
import { GridContainer, GridItem, Katex } from "./LatexEditor.styles";
import AceEditor from "react-ace";
import { Typography } from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import "ace-builds/src-noconflict/mode-latex";
import "ace-builds/src-noconflict/ext-language_tools";
import { useKatex } from "../../../helpers/hooks/useKatex";
import useDebouncedFunction from "../../../helpers/hooks/useDebouncedFunction";

export interface LatexEditorProps {
	controllerName: string;
	height?: string;
	displayTitle?: string;
	editorTitle?: string;
	pendingImages?: Record<string, string>;
}

export default function LatexEditor({
	height = "250px",
	editorTitle,
	displayTitle,
	controllerName,
	pendingImages = {},
}: LatexEditorProps) {
	const displayRef = useRef<HTMLDivElement | null>(null);
	const { getValues } = useFormContext();
	const [renderValue, setRenderValue] = useState(getValues(controllerName));
	useKatex(displayRef, [renderValue, displayRef]);
	const handleEditorChange = useDebouncedFunction(() => {
		setRenderValue(() => {
			let newValue = getValues(controllerName);
			Object.entries(pendingImages).forEach(([temp, image]) => {
				newValue = (newValue || "").replace(temp, image);
			});
			return newValue;
		});
	}, 300);

	return (
		<GridContainer container item spacing={4}>
			<GridItem item xs={12} lg={6}>
				{editorTitle && <Typography>{editorTitle}</Typography>}
				<AceEditorController controllerName={controllerName} height={height} onEditorChange={handleEditorChange} />
			</GridItem>
			<GridItem item xs={12} lg={6}>
				{displayTitle && <Typography>{displayTitle}</Typography>}
				<Katex ref={displayRef} sx={{ minHeight: height }} dangerouslySetInnerHTML={{ __html: renderValue }} />
			</GridItem>
		</GridContainer>
	);
}

function AceEditorController({
	height,
	controllerName,
	onEditorChange,
}: {
	height: string;
	controllerName: string;
	onEditorChange: (...args: any[]) => void;
}) {
	const { control } = useFormContext();
	const aceEditorValue = useWatch({
		control,
		name: controllerName,
	});
	useEffect(() => onEditorChange(), [aceEditorValue]);

	return (
		<div style={{ boxSizing: "border-box", border: "1px solid grey" }}>
			<Controller
				render={({ field: { value, onChange } }) => {
					return (
						<AceEditor
							style={{ minHeight: height, width: "100%" }}
							mode={"latex"}
							value={value}
							wrapEnabled
							maxLines={Infinity}
							onChange={onChange}
							setOptions={{
								enableLiveAutocompletion: false,
								fontSize: 14,
							}}
						/>
					);
				}}
				control={control}
				name={controllerName}
			/>
		</div>
	);
}
